import type { ImageAnnotatorController } from "@msuite/image-annotator";
import type { ImageAnnotatorImage } from "@msuite/utils/types";
import type { RefObject } from "react";
import useResizeObserver from "use-resize-observer";
import { useTranslation } from "../../localization";
import { Annotations } from "./annotations";
import { ImageCanvasContext } from "./context";
import { TextAnnotationEditor } from "./text-annotation-editor";

/** Props Interface */
interface AnnotationCanvasProps {
	src: string;
	image: ImageAnnotatorImage;
	fitSize: { width: number; height: number };
	annotator: ImageAnnotatorController;
	svgRef: RefObject<SVGSVGElement | null>;
	containerRef: RefObject<HTMLDivElement | null>;
}

export function AnnotationCanvas({
	src,
	image,
	fitSize,
	annotator,
	svgRef,
	containerRef,
}: AnnotationCanvasProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Effects */
	useResizeObserver({
		ref: containerRef as RefObject<HTMLDivElement>,
		onResize: ({ width, height }) => {
			if (!width || !height) return;
			annotator.handleOnSvgLayout({ width, height });
		},
	});

	/** Render */
	return (
		<ImageCanvasContext.Provider value={{ containerRef, imageUrl: src }}>
			<div
				ref={containerRef}
				className="relative"
				style={{ width: fitSize.width, height: fitSize.height }}
				onClick={(e) => e.stopPropagation()}
			>
				<img
					src={src}
					alt=""
					draggable={false}
					className="block select-none w-full h-full"
				/>
				<svg
					ref={svgRef}
					viewBox={`0 0 ${image.width} ${image.height}`}
					className="absolute inset-0 w-full h-full"
					role="img"
					aria-label={t("image_annotator.annotations")}
					style={{
						cursor: annotator.isReadOnly || annotator.tool === "select" ? undefined : "crosshair",
					}}
					onClick={annotator.isReadOnly ? undefined : annotator.handleOnContainerPress}
				>
					{annotator.showAnnotations && <Annotations />}
				</svg>
				{annotator.showAnnotations && !annotator.isReadOnly && <TextAnnotationEditor />}
			</div>
		</ImageCanvasContext.Provider>
	);
}
